<script setup lang="ts">
import type { BasicUser } from '~/models/User/BasicUser'
import type { SubjectCode } from '~/models/Subject'
import type { ReportFilterModel } from '~/models/Report'
import type { GradeCode } from '~/models/Grade'
import type { ReportType } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRoute, useRouter } from '#imports'
import { setTitle } from '~/utils/dom'
import useSchoolYear from '~/composables/useSchoolYear'
import { defaultMode, defaultSubject, getReportTag } from '~/composables/useAssignTags'
import useAssignmentApi from '~/api/assignmentApi'
import AuCallout from '~/components/ui/AuCallout.vue'
import StudentGraph from '~/components/reports/StudentGraph.vue'
import StudentExamGraph from '~/components/reports/StudentExamGraph.vue'
import ReportToolbar from '~/components/reports/ReportToolbar.vue'
import ReportHeader from '~/components/reports/ReportHeader.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { getReport } = useAssignmentApi()
const { currentSchoolYearInterval } = useSchoolYear()

setTitle(t('report.exam.title'))

const filterModel = ref<ReportFilterModel>({
  groupIdValue: undefined,
  gradeValue: undefined,
  subjectValue: undefined,
  contextValue: undefined,
  gradeTypeValue: undefined,
  intervalValue: undefined,
  mode: 'flyt_test',
  contentType: undefined,
})

const pagination = {
  pageSize: 1000,
  pageNumber: 1,
  includeUnansweredAssignments: true,
}

const hasAllParams = computed(() => !!filterModel.value.groupIdValue && !!filterModel.value.gradeValue && !!filterModel.value.subjectValue && !!filterModel.value.contextValue)
const isExamMode = computed(() => filterModel.value.mode === defaultMode)
const noResults = computed((): boolean => !isLoading.value && !!reportData.value && reportData.value?.length === 0)
const isReady = computed((): boolean => !isLoading.value && !!reportData.value && reportData.value?.length > 0)
const subjectCode = computed(() => (route.query.subject || defaultSubject) as SubjectCode)

const start = computed(() => {
  if (isExamMode.value) {
    return currentSchoolYearInterval.start?.toISO({ suppressMilliseconds: true })
  }
  return undefined
})

const end = computed(() => {
  if (isExamMode.value) {
    return currentSchoolYearInterval.end?.toISO({ suppressMilliseconds: true })
  }
  return undefined
})

watch(isExamMode, () => {
  if (isExamMode.value) {
    filterModel.value.subjectValue = subjectCode.value
    filterModel.value.contextValue = getReportTag(subjectCode.value)
  }
}, { immediate: true })

const { data: report, isLoading, isError } = useQuery({
  staleTime: Infinity,
  enabled: hasAllParams,
  retry: 1,
  queryKey: computed(() => ['report', filterModel.value.groupIdValue, start.value, end.value, filterModel.value.contentType, filterModel.value.contextValue, filterModel.value.subjectValue, filterModel.value.gradeValue]),
  queryFn: async () => await getReport(
    filterModel.value.groupIdValue,
    start.value,
    end.value,
    filterModel.value.contentType,
    filterModel.value.contextValue,
    filterModel.value.subjectValue,
    filterModel.value.gradeValue,
    pagination.pageNumber,
    pagination.pageSize,
    pagination.includeUnansweredAssignments,
  )
})

const reportData = computed(() => report?.value?.response ?? [])

const initializeFilterModelFromQuery = () => {
  let alternativeContext: string | undefined
  if (isExamMode.value) {
    alternativeContext = getReportTag(subjectCode.value)
  }

  filterModel.value = {
    groupIdValue: (route.query.groupId as string) || undefined,
    gradeValue: route.query.grade as GradeCode || undefined,
    subjectValue: subjectCode.value,
    contextValue: alternativeContext || (route.query.context as string) || undefined,
    gradeTypeValue: undefined,
    intervalValue: undefined,
    mode: defaultMode,
    contentType: 'flyt_test'
  }
}

const updateQueryParamsFromFilterModel = () => {
  const query = {
    ...route.query,
    groupId: filterModel.value.groupIdValue || undefined,
    grade: filterModel.value.gradeValue || undefined,
    subject: filterModel.value.subjectValue || undefined,
    context: filterModel.value.contextValue || undefined,
  }

  router.replace({ query })
}

watch(filterModel, updateQueryParamsFromFilterModel, { deep: true })
watch(
  () => route.query,
  () => {
    initializeFilterModelFromQuery()
  },
  { immediate: true }
)

const usersInResults = computed((): BasicUser[] => {
  if (noResults.value)
    return []

  const studentIds: string[] = []
  const users: BasicUser[] = []

  reportData.value?.forEach((assignment) => {
    assignment.responseSummary.forEach((summary) => {
      if (!studentIds.includes(summary.student.userId)) {
        studentIds.push(summary.student.userId)
        users.push(summary.student)
      }
    })
  })

  return users.sort((a, b) => a.lastName.localeCompare(b.lastName))
})
</script>

<template>
  <div class="bg-green-5 h-full">
    <header class="relative h-[320px] flex-col justify-center bg-green-50 text-white">
      <div class="max-w-screen-au relative mx-auto size-full px-4 pt-32 pb-10 transition-all sm:px-8 sm:pb-20">
        <h1
          class="fluid-text-3xl relative z-10 font-semibold"
          v-text="t('report.exam.title')"
        />
        <ReportToolbar v-model="filterModel as any" />
      </div>
    </header>
    <div
      v-if="isError"
      class="max-w-screen-au mx-auto px-4 py-8 sm:px-8"
    >
      <AuCallout
        variant="info"
        :title="t('report.messages.error.title')"
      >
        <p v-text="t('report.messages.error.message')" />
      </AuCallout>
    </div>
    <div
      v-if="isExamMode && !filterModel.gradeValue && !filterModel.groupIdValue"
      class="max-w-screen-au mx-auto px-4 py-8 sm:px-8"
    >
      <AuCallout
        variant="info"
        :title="t('report.messages.missingFilters.title')"
      >
        <p v-text="t('report.messages.missingFilters.message')" />
      </AuCallout>
    </div>
    <div
      v-else-if="!isExamMode && !hasAllParams"
      class="max-w-screen-au mx-auto px-4 py-8 sm:px-8"
    >
      <AuCallout
        variant="info"
        :title="t('report.toolbar.missing')"
      >
        <p v-if="!filterModel.subjectValue" v-text="t('report.toolbar.subjectDropdown')" />
        <p v-if="!filterModel.gradeValue" v-text="t('report.toolbar.gradeDropdown')" />
        <p v-if="!filterModel.groupIdValue" v-text="t('report.toolbar.groupDropdown')" />
        <p v-if="!filterModel.contextValue && filterModel.subjectValue && filterModel.gradeValue" v-text="t('report.toolbar.contextDropdown')" />
      </AuCallout>
    </div>
    <div
      v-else-if="noResults"
      class="max-w-screen-au mx-auto px-4 py-8 sm:px-8"
    >
      <AuCallout
        variant="info"
        :title="t('report.exam.noResults.title')"
      >
        <p v-text="t('report.exam.noResults.message')" />
      </AuCallout>
    </div>
    <div
      v-else-if="isReady && reportData"
      class="max-w-screen-au mx-auto px-4 py-8 sm:px-8"
    >
      <ReportHeader
        v-if="isExamMode"
        :report-data="reportData"
        :users-in-results="usersInResults"
        :group-id="filterModel.groupIdValue"
        :subject-code="subjectCode"
      />
      <div v-if="isExamMode" class="max-w-screen-au mx-auto mt-8 rounded-t-lg bg-white">
        <div class="bg-green-10 xs:grid-cols-2 grid h-14 rounded-t-lg pr-12">
          <div class="content-center p-4 uppercase">
            <p>{{ t('report.studentList.name') }}</p>
          </div>
          <div class="content-center py-4 uppercase">
            <p>{{ t('report.studentList.result') }}</p>
          </div>
        </div>
        <StudentExamGraph
          v-for="user in usersInResults"
          :key="user.userId"
          :report-data="reportData"
          :user="user"
          class="border-green-10 odd:bg-green-5/50 border-b-2 even:bg-white"
        />
      </div>
      <div v-if="!isExamMode" class="max-w-screen-au mx-auto mt-8 rounded-t-lg bg-white">
        <div class="bg-green-10 xs:grid-cols-2 grid h-14 rounded-t-lg">
          <div class="content-center p-4 uppercase">
            <p>{{ t('report.studentList.name') }}</p>
          </div>
          <div class="content-center py-4 uppercase">
            <p>{{ t('report.studentList.result') }}</p>
          </div>
        </div>
        <StudentGraph
          v-for="user in usersInResults"
          :key="user.userId"
          :report-data="reportData"
          :user="user"
          class="border-green-10 odd:bg-green-5/50 border-b-2 even:bg-white"
        />
      </div>
    </div>
  </div>
</template>
